import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNotifications } from "@/context/NotificationsContext";
import { CATEGORIES, URGENCY } from "@/lib/notifications";
import { fmtTs } from "@/lib/format";

const Pill = ({ active, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:bg-muted"}`}>{children}</button>
);

function Row({ e, unseen, onOpen }) {
  const u = URGENCY[e.urgency];
  return (
    <button onClick={onOpen} data-testid={`notif-${e.key}`} className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-border last:border-0 transition-colors hover:bg-muted/50 ${unseen ? "bg-red-50/40" : ""}`}>
      <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${unseen ? "bg-red-600" : "bg-transparent"}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm truncate ${unseen ? "font-semibold text-foreground" : "text-foreground"}`}>{e.title}</p>
        <p className="text-xs text-muted-foreground truncate">{e.kind} · {e.sub}</p>
      </div>
      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${u.cls}`}>{u.label}</span>
      <span className="text-[11px] text-muted-foreground shrink-0 w-32 text-right">{fmtTs(e.ts)}</span>
    </button>
  );
}

export default function Notifications() {
  const { events, isUnseen, seeItems, seeSection } = useNotifications();
  const nav = useNavigate();
  const [urg, setUrg] = useState("all");
  const [onlyNew, setOnlyNew] = useState(false);
  const shown = events.filter((e) => (urg === "all" || e.urgency === urg) && (!onlyNew || isUnseen(e)));
  const groups = useMemo(() => Object.keys(CATEGORIES).map((c) => ({ c, items: shown.filter((e) => e.cat === c) })).filter((g) => g.items.length), [shown]);
  const counts = Object.fromEntries(Object.keys(URGENCY).map((k) => [k, events.filter((e) => e.urgency === k && isUnseen(e)).length]));
  const open = (e) => { seeItems([e.key]); nav(CATEGORIES[e.cat].path); };
  return (
    <div>
      <PageHeader title="Notifications" subtitle="Everything new or changed in the last 30 days, grouped by area and ranked by business urgency. Red dot = not yet seen by you." 
        actions={<Button variant="outline" onClick={() => seeSection(Object.values(CATEGORIES).map((c) => c.section))} data-testid="notif-mark-all">Mark all seen</Button>} />
      <div className="flex flex-wrap gap-2 mb-5" data-testid="notif-filters">
        <Pill active={urg === "all"} onClick={() => setUrg("all")} testId="notif-filter-all">All</Pill>
        {Object.entries(URGENCY).map(([k, u]) => <Pill key={k} active={urg === k} onClick={() => setUrg(k)} testId={`notif-filter-${k}`}><span className={`inline-block h-2 w-2 rounded-full mr-1.5 ${u.dot}`} />{u.label}{counts[k] ? ` · ${counts[k]} new` : ""}</Pill>)}
        <Pill active={onlyNew} onClick={() => setOnlyNew(!onlyNew)} testId="notif-only-new">Unseen only</Pill>
      </div>
      <div className="space-y-5">
        {groups.map(({ c, items }) => {
          const cat = CATEGORIES[c]; const Icon = Icons[cat.icon] || Icons.Circle;
          const unseen = items.filter(isUnseen).length;
          return (
            <Card key={c} data-testid={`notif-group-${c}`}>
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
                <Icon className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold text-sm">{cat.label}</h3>
                {unseen > 0 && <span className="text-[10px] font-bold bg-red-600 text-white rounded-full px-2 py-0.5">{unseen} new</span>}
                <span className="text-xs text-muted-foreground">{items.length} total</span>
                <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs" onClick={() => seeSection([cat.section])} data-testid={`notif-seen-${c}`}>Mark seen</Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => nav(cat.path)}>Open <Icons.ArrowRight className="h-3 w-3 ml-1" /></Button>
              </div>
              {[...items].sort((a, b) => URGENCY[a.urgency].rank - URGENCY[b.urgency].rank || b.ts - a.ts).slice(0, 25).map((e) => <Row key={e.key} e={e} unseen={isUnseen(e)} onOpen={() => open(e)} />)}
              {items.length > 25 && <p className="text-xs text-muted-foreground px-4 py-2">+{items.length - 25} more in {cat.label}</p>}
            </Card>
          );
        })}
        {groups.length === 0 && <Card className="p-10 text-center text-muted-foreground" data-testid="notif-empty">Nothing to show.</Card>}
      </div>
    </div>
  );
}
